import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, signal, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { definePreset } from '@primeuix/themes';
import { BehaviorSubject, Observable } from 'rxjs';

import { OffcononAura } from '@offconon/shared/ui/themes';
import { WindowRefService } from '@offconon/shared/utils/services/window-ref';

import { rtlViewLang } from './langs.data';
import { Themes } from './theme.enum';

const Noir = definePreset(OffcononAura);

export const theme = {
  preset: Noir,
  options: {
    darkModeSelector: '.app-dark-theme',
    cssLayer: false,
  },
};

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private document = inject<Document>(DOCUMENT);
  private translate = inject(TranslateService);
  private platformId = inject(PLATFORM_ID);
  private windowRef = inject(WindowRefService);

  private readonly darkSchemePreference = '(prefers-color-scheme: dark)';
  rtlViewLang = rtlViewLang;
  public darkMode = false;
  private isMobileView = new BehaviorSubject<boolean>(false);
  private isMediumView = new BehaviorSubject<boolean>(false);
  isRtlview = new BehaviorSubject<boolean>(false);
  mobileView = this.isMobileView.asObservable();
  mediumView = this.isMediumView.asObservable();
  private darkModeSubject = new BehaviorSubject<boolean>(false);
  public darkModeObservable: Observable<boolean> = this.darkModeSubject.asObservable();
  contentWidth = signal(1024);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const prefersDark = this.windowRef.nativeWindow.matchMedia(this.darkSchemePreference).matches;
      const storedTheme = localStorage.getItem('theme');

      const useDark = storedTheme === Themes.dark || (!storedTheme && prefersDark);

      this.switchTheme(useDark);
      this.darkMode = useDark;
      this.darkModeSubject.next(useDark);
    }
  }

  updatedMobileViewSelection(data: boolean) {
    this.isMobileView.next(data);
  }

  updatedMediumViewSelection(data: boolean) {
    this.isMediumView.next(data);
  }

  public switchTheme(dark = false) {
    const element = document.querySelector('html');
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (dark) {
      element?.classList.add('app-dark-theme');
      element?.classList.remove('light');
      themeColorMeta?.setAttribute('content', '#000');
    } else {
      element?.classList.remove('app-dark-theme');
      themeColorMeta?.setAttribute('content', '#e6e8eb');
    }
    localStorage.setItem('theme', dark ? Themes.dark : Themes.light);
    this.darkModeSubject.next(dark);
  }

  public switchRtl(isRtl: boolean) {
    const layout = this.document.getElementById('offconon-layout-content') as HTMLDivElement;
    if (isRtl) {
      layout?.classList.add('ui-rtl');
      layout?.setAttribute('dir', 'rtl');
    } else {
      layout?.classList.remove('ui-rtl');
      layout?.setAttribute('dir', 'ltr');
    }
  }

  public setRtlView() {
    const isRtl = this.rtlViewLang.includes(this.translate.currentLang);
    this.switchRtl(isRtl);
    this.isRtlview.next(isRtl);
  }

  switchThemeInShadowDom(
    theme: string,
    containerSelector: string,
    shadowDomselector: string = '#page-builder-theme',
  ) {
    const offcononPageBuilder = document.querySelector(containerSelector);
    const shadowRoot = offcononPageBuilder?.shadowRoot;

    const themeLink: any = shadowRoot?.querySelector(shadowDomselector);

    if (themeLink && themeLink?.href) themeLink.href = theme + '.css';
  }
  public toggleDarkMode(): void {
    this.switchTheme(!this.darkModeSubject.value);
  }
}
